import React, { useRef, useCallback, useState } from 'react';
import WorkflowProgressBar from './WorkflowProgressBar';
import WorkflowStepToolTip from './WorkflowStepToolTip';
import useShortcut from 'react-scale/hooks/useShortcut';
import GuidedWorkflowFeedbackModal from './GuidedWorkflowFeedbackModal';
import { createShoutout } from 'react-scale/components/tour';
import { useMount, useUpdateEffect } from 'react-use-v13';
import useWorkflowStepActivationEffects from '../Sidebar/GuidedWorkflowPanel/useWorkflowStepActivationEffects';
import SAlert from 'react-s-alert';
import crypto from 'crypto';
import _ from 'lodash';
import { evaluateWorkflowDefinition } from '@scale/shared/taskerWorkflow/evaluateWorkflowDefinition';
import {
  getWorkflowAncestors,
  isCheckEveryWorkflowStep,
} from '@scale/shared/taskerWorkflow/TaskerWorkflow';
import { getFriendlyAnnotationId } from '@scale/shared/annotation';
import { WorkflowHook } from '../Sidebar/GuidedWorkflowPanel/useWorkflow';
import { LabelerStateHook } from 'react-scale/hooks/useLabelerState';
import { ReviewStateHook } from 'react-scale/components/scalers/tooling/reviews/useReviewState';
import {
  ScriptWorkflowDefinition,
  MarkdownWorkflowDefinition,
} from '@scale/shared/taskerWorkflow/TaskerWorkflowDefinition';
import { GuidedWorkflowAnnotationData } from '@scale/shared/taskerWorkflow/types';
import { makeStyles } from 'react-scale/components/design-system';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'var(--color-RemoGray01)',
    position: 'relative',
  },
  title: {
    padding: 8,
  },
  workflowStepToolTip: {
    position: 'absolute',
    top: 50,
    left: 50,
    zIndex: 1,
  },
  feedbackButton: {
    alignSelf: 'stretch',
    marginRight: '12px',
    marginLeft: 'auto',
    paddingTop: '2px',
  },
});

interface GuidedWorkflowBarProps {
  workflowDefinition: ScriptWorkflowDefinition | MarkdownWorkflowDefinition;
  workflowHook: WorkflowHook;
  annotations: GuidedWorkflowAnnotationData[];
  labelerStateHook?: LabelerStateHook;
  reviewStateHook?: ReviewStateHook;
}

const GuidedWorkflowBar: React.FC<GuidedWorkflowBarProps> = props => {
  const classes = useStyles();

  const {
    workflow,
    updateWorkflow,
    toggleIsGuidedWorkflowTooltipOpen,
    getWorkflowName,
    workflowState,
    setActiveCheckEveryAnnotation,
    setActiveStepId,
  } = props.workflowHook;

  const refreshWorkflow = () => {
    if (props.workflowDefinition) {
      try {
        const newWorkflow = evaluateWorkflowDefinition(props.annotations, props.workflowDefinition);
        updateWorkflow(newWorkflow!);
      } catch (error) {
        updateWorkflow(undefined);
      }
    } else {
      updateWorkflow(undefined);
    }
  };

  // Use a hash to avoid render on vertex move
  const annotationsHash = crypto
    .createHash('sha1')
    .update(
      _.map(props.annotations, annotation => `${annotation.uuid}-${annotation.label}`).join('-'),
    )
    .digest('hex');
  const [annotationsDidLoad, setAnnotationsDidLoad] = useState<boolean>(false);

  useMount(() => {
    refreshWorkflow();
  });

  useUpdateEffect(() => {
    refreshWorkflow();
    const blankHash = crypto.createHash('sha1').update('').digest('hex');
    if (annotationsHash !== blankHash && !annotationsDidLoad) {
      setAnnotationsDidLoad(true);
    }
  }, [annotationsHash, annotationsDidLoad, setAnnotationsDidLoad]);

  useUpdateEffect(() => {
    if (annotationsDidLoad && props.reviewStateHook) {
      // Clear review state to sync with GWF when active so they start off in sync
      // todo: deprecate once GWF state is in local storage
      props.reviewStateHook.clearAnnotationReviewStates(
        _.map(props.annotations, annotation => annotation.uuid),
      );
    }
  }, [annotationsDidLoad]);

  // TODO: @Alan remove this later
  const alertActivationEffect = useCallback((message: string) => {
    SAlert.info(<span>{message}</span>);
  }, []);

  // TODO: @Alan remove this later
  useWorkflowStepActivationEffects(workflow, props.labelerStateHook, alertActivationEffect);

  const selectedAnnotationIds = props.labelerStateHook?.selectedAnnotationIds ?? new Set();
  const selectedAnnotationId =
    selectedAnnotationIds?.size === 1 ? selectedAnnotationIds.values().next().value : undefined;

  // Set Active State for CheckEvery
  // TODO: Make this not happen everytime selectedAnnotationID is here
  useUpdateEffect(() => {
    if (workflow && !workflowState.lockedCheckEveryAnnotation) {
      const hasCheckEveryAncestor = _.find(
        getWorkflowAncestors(workflow, workflowState.currentStep.id),
        node => isCheckEveryWorkflowStep(node),
      );
      if (hasCheckEveryAncestor) {
        const linkedLabelType: string = hasCheckEveryAncestor.linkedLabelType!;
        const selectedAnnotation: { uuid: string; label: string } | undefined = _.find(
          props.annotations,
          {
            uuid: selectedAnnotationId ?? undefined,
          },
        );
        // If we deselect, we want to clear activeAnnotation
        if (!selectedAnnotation) {
          setActiveCheckEveryAnnotation(null);
        }
        if (linkedLabelType === selectedAnnotation?.label) {
          setActiveCheckEveryAnnotation(selectedAnnotation.uuid);
          setActiveStepId(
            hasCheckEveryAncestor.id + '-' + getFriendlyAnnotationId(selectedAnnotation.uuid),
          );
        }
      }
    }
  }, [selectedAnnotationId]);

  // SHORTCUTS
  useShortcut({
    trigger: 't',
    description: 'Toggle Guided Workflow ToolTip',
    action: () => toggleIsGuidedWorkflowTooltipOpen(),
  });

  useShortcut({
    trigger: 'shift+i',
    description: 'Show Workflow Step Instructions',
    action: props.workflowHook.toggleIsGuidedWorkflowInstructionsPanelOpen,
    disabled: !workflow,
  });

  useShortcut({
    trigger: 'shift+n',
    description: 'Next Step',
    action: props.workflowHook.moveNextSiblingStep,
  });

  useShortcut({
    trigger: 'shift+b',
    description: 'Previous Step',
    action: props.workflowHook.movePreviousSiblingStep,
  });

  const guidedWorkflowBarContainRef = useRef<HTMLDivElement>(null);
  const workflowStepContainerRef = useRef<HTMLDivElement>(null);

  const barBoundingBox = guidedWorkflowBarContainRef?.current?.getBoundingClientRect();
  const stepBoundingBox = workflowStepContainerRef?.current?.getBoundingClientRect();

  // The Workflow ToolTip is 260 wide, so we subtract half the width.
  const workflowStepToolTipLeft =
    barBoundingBox && stepBoundingBox ? stepBoundingBox.x + stepBoundingBox.width / 2 - 130 : 0;
  const workflowStepToolTipTop = barBoundingBox && stepBoundingBox ? barBoundingBox.height + 20 : 0;

  const feedbackButtonShoutout = createShoutout({
    storageName: 'GWF-Feedback-Button',
    showing: 'once',
  });

  return workflow ? (
    <div className={classes.container} ref={guidedWorkflowBarContainRef}>
      <div className={classes.title}>{getWorkflowName()}</div>
      <div>
        <WorkflowProgressBar
          workflowHook={props.workflowHook}
          workflowStepContainerRef={workflowStepContainerRef}
        />
      </div>
      {workflowState.isGuidedWorkflowTooltipOpen && (
        <div
          className={classes.workflowStepToolTip}
          style={{ left: workflowStepToolTipLeft, top: workflowStepToolTipTop }}
        >
          <WorkflowStepToolTip
            workflowHook={props.workflowHook}
            labelerStateHook={props.labelerStateHook}
            reviewStateHook={props.reviewStateHook}
          />
        </div>
      )}
      <div className={classes.feedbackButton}>
        <feedbackButtonShoutout.Tooltip
          arrow="right"
          title="Guided Workflow Feedback"
          content={<>Click here to leave feedback on the new Guided Workflow Bar!</>}
        >
          {props.labelerStateHook && (
            <GuidedWorkflowFeedbackModal workflowHook={props.workflowHook} />
          )}
        </feedbackButtonShoutout.Tooltip>
      </div>
    </div>
  ) : null;
};
export default React.memo(GuidedWorkflowBar);
